import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import wordmarkUrl from "@/assets/backland-wordmark.svg";
import { useInAppReferrer } from "@/lib/useInAppReferrer";

type Props = {
  /**
   * Fallback secondary link when the visitor has no in-app referrer.
   * Pass `null` to hide the secondary link entirely in that case
   * (used on the blog index — the wordmark CTA is enough).
   */
  fallbackBack?: { to: string; label: string } | null;
  backOverride?: { to: string; label: string } | null;
};

export default function BlogHeader({ fallbackBack = null, backOverride = null }: Props) {
  const referrer = useInAppReferrer();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const back = backOverride ?? (referrer
    ? { to: referrer.path, label: `Back to ${referrer.label}` }
    : fallbackBack);

  return (
    <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-5xl mx-auto px-6 flex items-center justify-between gap-4 py-4">
        <Link to="/" aria-label="Backland — visit site" className="inline-flex items-center">
          <img
            src={wordmarkUrl}
            alt="Backland"
            className="h-5 md:h-6 w-auto opacity-90 hover:opacity-100 transition-opacity"
          />
        </Link>

        {mounted && back ? (
          <a
            href={back.to}
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
          >
            {backOverride ? back.label : `← ${back.label}`}
          </a>
        ) : null}
      </div>
    </div>
  );
}
